import { setUserId, trackPageMeta } from '@farmnet/tag-manager';

/**
 * @ngdoc provider
 * @name farmnet.base.tracking.tracker
 * @description
 * TODO _Please update the description and dependencies._
 *
 **/
function TrackerProvider() {
  /**
   * If set to true tracking won't initialize, i.e. the piwik script
   * won't be loaded and hence no data is send to the server.
   * (Still tracking log messages for localhost are visible.)
   * @type {boolean}
   */
  this.disabled = false;

  /**
   * Piwik servers setup for serving from host
   * @type {Array}
   */
  this.servers = [];

  /**
   * If the user should be tracked, so he is unique to piwik
   * by saving his UUID/login-name
   * @type {boolean}
   */
  this.trackUser = true;

  /**
   * If links should be tracked automatically
   * @type {boolean}
   */
  this.enableLinkTracking = true;

  /**
   * Ids for predefined goals. Should be configured at config time (in tracker.config.js).
   * buy, test and freemium have as properties the lower-case name of the service key.
   * Other service keys can be added without further adaption.
   * @type {{register: number, freemium: object, buy: object, test: object}}
   */
  this.goalIds = {
    register: 1,
    free: {},
    buy: {},
    test: {},
    login: {}
  };

  /**
   * Installs the piwik.js from the appropriate server.
   * If no server corresponds to the current hostname no script will be installed
   * and nothing will be tracked.
   * The method must be run at runtime - not config time, since it needs some services
   * which are not available at config time.
   * @param {ng.$document} $document
   * @param {ng.$location} $location
   * @param {farmnet.base.tracking.piwik} piwik
   */
  this.init = function($document, $location, $log, md5, piwik) {
    //// do nothing if tracking is disabled
    // no script will be loaded
    if (this.disabled) {
      $log.info('tracking is disabled');
      return;
    }

    //// find server for hosting domain
    var host = $location.host(),
      hostMd5 = md5.createHash(host),
      serverUrl = null,
      siteId = null;

    for (var i = 0; i < this.servers.length; i++) {
      var server = this.servers[i];
      for (var j = 0; j < server.sites.length; j++) {
        var site = server.sites[j];
        if (
          site.hosts.indexOf(host) !== -1 ||
          site.hosts.indexOf(hostMd5) !== -1
        ) {
          serverUrl = server.url;
          siteId = site.id;
          break; // site found
        }
      }

      // a server was found?
      if (serverUrl !== null) {
        break; // we are done
      }
    }

    //// no server found? => do nothing
    if (!serverUrl) {
      $log.error(`No Piwik server found for hostname ${host}`);
      return;
    }

    //// initial values
    // server and site id
    piwik.setTrackerUrl(serverUrl + '/piwik.php');
    piwik.setSiteId(siteId);

    // automatic link tracking by piwik
    if (this.enableLinkTracking) {
      piwik.enableLinkTracking();
    }

    //// do NOT track page view here since we will have a wrong title (with uuid instead of :id etc.)
    // initial track will be on $routeChangeEnd

    //// insert script for async loading
    var piwikScript = serverUrl + '/piwik.js';
    var document = $document[0],
      piwikScriptElement = document.createElement('script'),
      firstScriptElement = document.getElementsByTagName('script')[0];

    piwikScriptElement.type = 'text/javascript';
    piwikScriptElement.defer = true;
    piwikScriptElement.async = true;
    piwikScriptElement.src = piwikScript;
    firstScriptElement.parentNode.insertBefore(
      piwikScriptElement,
      firstScriptElement
    );
  };

  // push current `uiCustomerType` once on app initialization
  trackPageMeta({
    uiCustomerType: 'active'
  });

  var trackerProvider = this;

  this.$get = [
    '$window',
    '$document',
    '$location',
    '$log',
    '$route',
    'md5',
    'credentials',
    'piwik',
    function TrackerFactory(
      $window,
      $document,
      $location,
      $log,
      $route,
      md5,
      credentials,
      piwik,
    ) {
      // init provider at runtime (since some services are not available at config time)
      trackerProvider.init($document, $location, $log, md5, piwik);

      function Tracker() {
        // save myself
        var self = this;

        /**
         * Logs with info on localhost.
         * @private
         */
        // TODO: [] Move to helper.ts?
        function log(/*...*/) {
          var host = $location.host();
          if (
            host === 'localhost' ||
            host === '127.0.0.1' ||
            host === '0.0.0.0' ||
            host === 'fe'
          ) {
            var mainArguments = Array.prototype.slice.call(arguments);
            if (trackerProvider.disabled) {
              mainArguments.push('[Actual tracking is disabled!]');
            }
            $log.info.apply(null, mainArguments);
          }
        };

        /**
         * Tracks the current user if user tracking is enabled.
         * See {@link TrackerProvider#trackUser}.
         */
        this.trackUser = () => {
          // track users by userId md5 hash
          if (trackerProvider.trackUser && credentials.userId) {
            const userIdHash = md5.createHash(credentials.userId);
            const userUuidHash = credentials.person ? md5.createHash(credentials.person.id) : '';

            log('track user:', userIdHash);

            // gtm `uiUserId` meta data
            setUserId(userUuidHash); // on every page the user is logged in
            piwik.setUserId(userIdHash);

            // do it just once
            trackerProvider.trackUser = false;
          }
        };

        this.trackMetadata = () => {
          try {
            const isLoggedIn = app.credentials ? app.credentials.loggedIn.toString() : ''; // unit tests need `app.credenetials`
            const companyIdHash = credentials.company ? md5.createHash(credentials.company.companyId) : '';
            const zipCode = _isZipCodeAvailable() ? credentials.person.address[0].zip : 'hello';
            const isCommerciallyContactable = credentials.person ? credentials.person.agreementCommercialContact.toString() : '';

            log('track meta data');

            // TODO: [] Move to helpers.js
            const intViewportWidth = window.innerWidth;
            let breakpoint = 'undefined';
            let currentDashboard = getCookieValue('user.lastUsedDashboard');

            if (intViewportWidth >= 1200) {
              breakpoint = 'extra-large';
            } else if (intViewportWidth >= 992) {
              breakpoint = 'large';
            } else if (intViewportWidth >= 768) {
              breakpoint = 'medium';
            } else if (intViewportWidth >= 576) {
              breakpoint = 'small';
            } else if (intViewportWidth >= 0) {
              breakpoint = 'extra-small';
            }

            function getCookieValue(a) {
              const b = document.cookie.match('(^|;)\\s*' + a + '\\s*=\\s*([^;]+)');
              return b ? b.pop() : '';
            };

            currentDashboard === 'dashboard' ? currentDashboard = 'crop' : currentDashboard = 'cattle';

            const currentPage = window.location.hash === '#/dashboard' ? 'home' : 'other';

            trackPageMeta(
              {
                language: navigator.language.split('-')[0], // on all sites
                uiLoggedIn: isLoggedIn, // only during log in
                environment: document.location.host.split('.')[0].split(':')[0], // on all sites
                pageType: currentPage, // on all sites
                breakPoint: breakpoint, // on all sites
                uiFarmSize: '', // only during login
                uiFarmType: currentDashboard, // only during login
                uiCompanyId: companyIdHash, // only during login
                uiPostalCode: zipCode, // only during login
                uiNewsletterSubscriber: isCommerciallyContactable, // only during login and account registration
                uiEmployees: '' // only during login
              }
            );
          }
          catch(error) {
            console.error('[TrackerProvider] Could not GTM trackMetadata():', error);
          }
        };

        // track gtm page meta on loggin site and forgotpwd
        if (window.location.hash === '#/') this.trackMetadata();

        function _isZipCodeAvailable() {
          return app.credentials.person
            && (app.credentials.person.address.length > 0)
            && !!app.credentials.person.address[0].zip; // Bang, bang!
        };

        /**
         * Tracks all custom variables.
         */
        this.trackCustomVariables = function() {
          //// Locale
          log('track custom variable: ', 1, 'Locale', credentials.locale);
          piwik.setCustomVariableForVisit(1, 'Locale', credentials.locale);

          //// Customer Groups
          // BuyerGroup (BUYER, TESTER, FREE, NONE)
          log(
            'track custom variable: ',
            2,
            'BuyerGroup',
            credentials.customerGroups.buyerGroup
          );
          piwik.setCustomVariableForVisit(
            2,
            'BuyerGroup',
            credentials.customerGroups.buyerGroup
          );
          // AmountGroup (<= 50ha, 50 < x <= 100ha, 100 < x < 300ha, > 300h)
          log(
            'track custom variable: ',
            3,
            'AmountGroup',
            credentials.customerGroups.amountGroup
          );
          piwik.setCustomVariableForVisit(
            3,
            'AmountGroup',
            credentials.customerGroups.amountGroup
          );
        };

        /**
         * Tracks a path. The current url (inclusive the route part) will
         * be tracked as custom url and the path as document title.
         * It will also call {@link #trackuser}.
         * @param {string} path
         */
        this.trackPath = function(path) {
          log('track route: {0}'.format(path));

          this.trackUser();
          this.trackMetadata();
          this.trackCustomVariables();
          piwik.trackPageView(path, this.getUrlWithoutUuids());
        };

        /**
         * Returns current URL without UUIDs.
         */
        this.getUrlWithoutUuids = function() {
          //this gets the full url
          var url = $location.absUrl();
          //replace all UUIDs with ':id'
          url = url.replace(
            /[a-z0-9]+-[a-z0-9]+-[a-z0-9]+-[a-z0-9]+-[a-z0-9]+/,
            ':id'
          );
          return url;
        };

        /**
         * Returns the file name in the current url.
         * E.g. https://app.365farmnet.com/dist/index.html#/core/farm will
         * return index.html.
         * @returns {string}
         */
        this.getFilenameInUrl = function() {
          //this gets the full url
          var url = $location.absUrl();
          //this removes the anchor at the end, if there is one
          url = url.substring(
            0,
            url.indexOf('#') === -1 ? url.length : url.indexOf('#')
          );
          //this removes the query after the file name, if there is one
          url = url.substring(
            0,
            url.indexOf('?') === -1 ? url.length : url.indexOf('?')
          );
          //this removes everything before the last slash in the path
          url = url.substring(url.lastIndexOf('/') + 1, url.length);
          //return
          return url;
        };

        /**
         * Calculates a path from a route. The path will be constructed from the
         * route template (orginalPath, e.g. /core/field/:id).
         * @param {ngRoute.$route} route
         * @returns {string}
         */
        this.pathFromRoute = function(route) {
          return (
            this.getFilenameInUrl() +
            '/' +
            (route.$$route ? route.$$route.originalPath : '')
          );
        };

        /**
         * Tracks a route. It will generate a path from the route and track this
         * path. See {@link #trackPath}
         * @param {ngRoute.$route} route
         * @returns {string}
         */
        this.trackRoute = function(route) {
          this.trackPath(this.pathFromRoute(route));
        };

        /**
         * Tracks a visit to a page by a given URL and Title
         * @param {string} [title]
         * The document title reported to Piwik.
         * @param {string} [url]
         * The page's (route's) url reported to Piwik.
         * This can also be a virtual custom URL that defines a specific user action.
         */
        this.trackPageView = function(title, url) {
          log('track page view: url: {1} - title: {0}'.format(url, title));

          piwik.trackPageView(title, url);
        };

        /**
         * Track an event.
         * @param {string} category
         * @param {string} action
         * @param {string} [name]
         * @param {number} [value]
         */
        this.trackEvent = function(category, action, name, value) {
          log(
            'track event: category: {0} - action: {1} - name:{2} - value: {3}'.format(
              category,
              action,
              name,
              value
            )
          );

          piwik.trackEvent(category, action, name, value);
        };

        /**
         * Tracks an event with the current route. (See {@link #trackRoute}
         * as category.
         * @param action
         * @param [name]
         * @param [value]
         */
        this.trackEventWithRoute = function(action, name, value) {
          var category = $route.current.$$route.originalPath;
          this.trackEvent(category, action, name, value);
        };

        /**
         * Tracks the showing of the help dialog as an event 'online-help show'.
         * @param context
         * @param language
         */
        this.trackHelp = function(context, language) {
          this.trackEvent('online-help show', context, language);
        };

        /**
         * Manually log a conversion for the goal with id goalId, passing in the custom
         * revenue customRevenue if specified.
         * @param {integer} goalId
         * @param {number} [customRevenue]
         */
        this.trackGoal = function(goalId, customRevenue) {
          log(
            'track goal with id {0} and custom revenue {1}'.format(
              goalId,
              customRevenue || '[none]'
            )
          );
          piwik.trackGoal(goalId, customRevenue);
        };

        /**
         * Tracks a successful registration as a goal.
         */
        this.trackRegistration = function() {
          this.trackGoal(trackerProvider.goalIds.register);
        };

        /**
         * Private helper to track the test or buy/book of a (free) component
         * @param {'free'|'buy'|'test'} type
         * @param {string} serviceKey
         * @param {number} [price]
         */
        function trackComponentGoal(type, serviceKey, price) {
          //// Requirements
          // check type
          if (!(type === 'free' || type === 'buy' || type === 'test')) {
            $log.error(
              'Tracker, component goal tracking: the type "{0}" is not supported'.format(
                type
              )
            );
          }

          // check if type exists in goalIds
          if (!trackerProvider.goalIds.hasOwnProperty(type)) {
            $log.error(
              'Tracker, component goal tracking: the type "{0}" does not exist in goalIds'.format(
                type
              )
            );
          }

          var goalIdsForType = trackerProvider.goalIds[type];

          // to lower case
          var serviceKeyProperty = serviceKey.toLowerCase();

          // check if service key exists
          if (!goalIdsForType.hasOwnProperty(serviceKeyProperty)) {
            $log.error(
              'Tracker, component goal tracking: no goal id for service key {0} ' +
                'found for type "{1}"'.format(serviceKey, type)
            );
          }

          //// all ok, can track
          var goalId = goalIdsForType[serviceKeyProperty];
          if (price === undefined) {
            self.trackGoal(goalId);
          } else {
            self.trackGoal(goalId, price);
          }
        }

        /**
         * Tracks the booking of a free component with the given service key.
         * A goal id for the service key must be set as a property of
         * {@link TrackerProvider.goalIds.free}
         * @param serviceKey
         */
        this.trackBookFreeComponent = function(serviceKey) {
          trackComponentGoal('free', serviceKey);
        };

        /**
         * Tracks the purchase of a component with the given service key.
         * A goal id for the service key must be set as a property of
         * {@link TrackerProvider.goalIds.buy}
         * @param serviceKey
         */
        this.trackBuyComponent = function(serviceKey, price) {
          trackComponentGoal('buy', serviceKey, price);
        };

        /**
         * Tracks the test of a component with the given service key.
         * A goal id for the service key must be set as a property of
         * {@link TrackerProvider.goalIds.test}
         * @param serviceKey
         */
        this.trackTestComponent = function(serviceKey) {
          trackComponentGoal('test', serviceKey);
        };
      }

      return new Tracker();
    }
  ];
}

export default TrackerProvider;
